import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Panel, StatusBadge, EmptyState } from "@/components/admin/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ConversionDialog } from "@/components/admin/ConversionDialog";
import { LeadBoard } from "@/components/admin/LeadBoard";
import { AddLeadDialog } from "@/components/admin/AddLeadDialog";
import { StageScheduleDialog } from "@/components/admin/StageScheduleDialog";
import { LEAD_STATUSES, SCHEDULE_STAGES, waLink, waMessage } from "@/lib/constants";
import { Phone, MessageCircle, StickyNote, CalendarPlus, ShieldCheck, LayoutGrid, List, Plus } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [status, setStatus] = useState(ALL);
  const [noteLead, setNoteLead] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [fuLead, setFuLead] = useState(null);
  const [fuDate, setFuDate] = useState("");
  const [fuNotes, setFuNotes] = useState("");
  const [saleLead, setSaleLead] = useState(null);
  const [pendingStage, setPendingStage] = useState(null);
  const [view, setView] = useState("table");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const p = status !== ALL ? `?status=${status}` : "";
    const [l, t] = await Promise.all([api.get(`/leads${p}`), api.get("/team")]);
    setLeads(l.data); setTeam(t.data);
  };
  useEffect(() => { load(); }, [status]);

  const teamName = (id) => team.find((t) => t.member_id === id)?.name || "Unassigned";
  const updateLead = async (id, body) => { await api.patch(`/leads/${id}`, body); toast.success("Lead updated"); load(); };
  const changeStage = async (l, s) => {
    if (SCHEDULE_STAGES.includes(s) && s !== l.status) { setPendingStage({ lead: l, stage: s, name: l.name }); return; }
    await updateLead(l.lead_id, { status: s });
    if (s === "Sale done") { setSaleLead({ visitor_id: l.visitor_id, lead_id: l.lead_id, sales_executive: l.assigned_to, name: l.name }); return; }
    if (view === "board" && !SCHEDULE_STAGES.includes(l.status) && l.status !== s) {
      toast(`${l.name} moved to ${s}`, { action: { label: "Undo", onClick: () => updateLead(l.lead_id, { status: l.status }) }, duration: 6000 });
    }
  };
  const saveNote = async () => { await api.post(`/leads/${noteLead.lead_id}/notes`, { text: noteText }); toast.success("Note added"); setNoteLead(null); setNoteText(""); load(); };
  const saveFu = async () => {
    await api.post(`/leads/${fuLead.lead_id}/followup`, { followup_date: new Date(fuDate).toISOString(), notes: fuNotes });
    toast.success("Follow-up scheduled"); setFuLead(null); setFuDate(""); setFuNotes(""); load();
  };

  return (
    <div>
      <PageHeader title="Leads & Pipeline" subtitle={`${leads.length} leads`}>
        <Button onClick={() => setAdding(true)} className="btn-orange h-10 rounded-xl" data-testid="add-lead-btn"><Plus size={16} /> Add Lead</Button>
        <div className="flex rounded-xl bg-white border border-slate-200 p-1" data-testid="lead-view-toggle">
          {[["table", List, "Table"], ["board", LayoutGrid, "Board"]].map(([k, Icon, label]) => (
            <button key={k} onClick={() => { setView(k); if (k === "board") setStatus(ALL); }} data-testid={`lead-view-${k}-btn`}
              className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors ${view === k ? "bg-[#081226] text-white" : "text-slate-500 hover:text-[#081226]"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {view === "table" && (
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px] h-10 rounded-xl bg-white" data-testid="lead-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All Statuses</SelectItem>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        )}
      </PageHeader>

      {view === "board" ? (
        <LeadBoard leads={leads} teamName={teamName} onMove={changeStage} onOpen={(l) => navigate(`/admin/visitors/${l.visitor_id}`)} />
      ) : (
      <Panel>
        {leads.length === 0 ? <EmptyState text="No leads found" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-3 px-2">Lead</th><th className="px-2">Purpose / Interests</th><th className="px-2">Source</th>
                  <th className="px-2">Assigned</th><th className="px-2">Status</th><th className="px-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.lead_id} data-testid="lead-row" className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-2">
                      <p className="font-semibold text-[#081226] cursor-pointer" onClick={() => navigate(`/admin/visitors/${l.visitor_id}`)}>
                        <span className="inline-flex items-center gap-1">{l.name}{l.ekyc_verified && <ShieldCheck size={14} className="text-emerald-500 shrink-0" data-testid="lead-verified-tick" />}</span>
                      </p>
                      <p className="font-mono-num text-xs text-[#B8860B]">{l.visitor_id} · {l.mobile}</p>
                    </td>
                    <td className="px-2 max-w-[200px]"><p className="font-medium text-slate-700">{l.purpose}</p><p className="text-xs text-slate-400 truncate">{l.interests?.join(", ")}</p></td>
                    <td className="px-2 text-slate-500">{l.source}</td>
                    <td className="px-2">
                      <Select value={l.assigned_to || ""} onValueChange={(v) => updateLead(l.lead_id, { assigned_to: v })}>
                        <SelectTrigger className="h-8 w-[130px] rounded-lg text-xs" data-testid="lead-assign-select"><SelectValue placeholder="Assign…" /></SelectTrigger>
                        <SelectContent>{team.filter((t) => ["Sales Executive", "Manager"].includes(t.role)).map((t) => <SelectItem key={t.member_id} value={t.member_id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-2">
                      <Select value={l.status} onValueChange={(v) => changeStage(l, v)}>
                        <SelectTrigger className="h-8 w-[140px] rounded-lg text-xs" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-2">
                      <div className="flex items-center justify-end gap-1">
                        <a href={waLink(l.mobile, waMessage(l.name, l.interests?.[0] || l.purpose))} target="_blank" rel="noreferrer" data-testid="lead-whatsapp-action-btn" className="p-2 text-[#25D366]" title="WhatsApp"><MessageCircle size={17} /></a>
                        <a href={`tel:${l.mobile}`} className="p-2 text-[#081226]" title="Call" data-testid="lead-call-btn"><Phone size={17} /></a>
                        <button onClick={() => setNoteLead(l)} className="p-2 text-slate-500" title="Add note" data-testid="lead-note-btn"><StickyNote size={17} /></button>
                        <button onClick={() => setFuLead(l)} className="p-2 text-[#D95D1E]" title="Schedule follow-up" data-testid="lead-followup-btn"><CalendarPlus size={17} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      )}

      <AddLeadDialog open={adding} onClose={() => setAdding(false)} team={team} onSaved={load} />
      <StageScheduleDialog pending={pendingStage} onCancel={() => setPendingStage(null)}
        onConfirm={async (scheduled_at, stage_notes) => { await updateLead(pendingStage.lead.lead_id, { status: pendingStage.stage, scheduled_at, stage_notes }); setPendingStage(null); }} />
      <ConversionDialog open={!!saleLead} onClose={() => setSaleLead(null)} initial={saleLead} team={team} onSaved={load} title={`Sale Details — ${saleLead?.name || ""}`} />

      <Dialog open={!!noteLead} onOpenChange={(o) => !o && setNoteLead(null)}>
        <DialogContent><DialogHeader><DialogTitle>Add Note — {noteLead?.name}</DialogTitle></DialogHeader>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Write a note…" rows={4} data-testid="note-textarea" />
          {noteLead?.notes?.length > 0 && <div className="max-h-40 overflow-y-auto space-y-2 mt-2">{noteLead.notes.map((n, i) => <div key={i} className="text-xs bg-slate-50 p-2 rounded-lg"><p>{n.text}</p><p className="text-slate-400 mt-1">{n.by} · {n.at?.slice(0, 16).replace("T", " ")}</p></div>)}</div>}
          <DialogFooter><Button onClick={saveNote} disabled={!noteText.trim()} data-testid="note-save-btn" className="btn-orange">Save Note</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fuLead} onOpenChange={(o) => !o && setFuLead(null)}>
        <DialogContent><DialogHeader><DialogTitle>Schedule Follow-up — {fuLead?.name}</DialogTitle></DialogHeader>
          <label className="text-sm font-semibold">Follow-up date</label>
          <Input type="datetime-local" value={fuDate} onChange={(e) => setFuDate(e.target.value)} data-testid="followup-date-input" />
          <Textarea value={fuNotes} onChange={(e) => setFuNotes(e.target.value)} placeholder="Notes…" rows={3} data-testid="followup-notes-textarea" />
          <DialogFooter><Button onClick={saveFu} disabled={!fuDate} data-testid="followup-save-btn" style={{ background: "linear-gradient(90deg,#D95D1E,#C04A0E)" }}>Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
